import { AsyncPipe } from "@angular/common";
import { Component, Input, OnChanges, SimpleChanges, inject, signal } from "@angular/core";
import { RiskDataService } from "@app/risk/services/RiskDataService";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixLinkedItemTableComponent } from "@app/shared/organisms/cerrix-linked-item-table/cerrix-linked-item-table.component";
import { CerrixLinkItemsDialogComponent } from "@app/shared/organisms/cerrix-link-items-dialog/cerrix-link-items-dialog.component";
import { LinkableItem } from "@app/shared/organisms/cerrix-link-items-dialog/models/linkable-item";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { LinkedRiskRowComponent } from "@app/assetmanagement/templates/linked-items/risks/linked-risk-row.component";
import { LinkedControlRowComponent } from "@app/assetmanagement/templates/linked-items/controls/linked-control-row.component";
import {
    LinkedControlTableDetails,
    LinkedControlTableItem,
} from "@app/assetmanagement/templates/linked-items/controls/linked-control-table.models";
import { ControlDataService } from "@app/controls/services/control.data.service";
import {
    LinkedRiskTableDetails,
    LinkedRiskTableItem,
} from "@app/assetmanagement/templates/linked-items/risks/linked-risk-table.models";
import {
    TranslatePipe,
    injectTranslation,
    provideTranslationScope,
} from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { Pages } from "@constants/pages/Pages";
import { ToastrService } from "ngx-toastr";
import { TabService } from "../../../../services/tabs/TabService";
import { Observable, catchError, firstValueFrom, map, of, startWith, throwError } from "rxjs";

type LinkedRisksState =
    | { status: "loading" | "error" }
    | { status: "loaded"; items: readonly LinkedRiskTableItem[] };

type LinkedControlsState =
    | { status: "loading" | "error" }
    | { status: "loaded"; items: readonly LinkedControlTableItem[] };

// The linked controls list already carries every detail field, so no separate details request is needed.
// When a details endpoint exists, replace the `of(item).pipe(map(...))` in loadControlDetails with that request.
function mapControlRowToDetails(item: LinkedControlTableItem): LinkedControlTableDetails {
    return {
        identifier: item.identifier,
        name: item.name,
        score: item.score,
        scoreColor: item.scoreColor,
        description: item.description,
        organization: item.organization,
        controlOwner: item.controlOwner,
    };
}

@Component({
    selector: "app-asset-linked-items-section",
    templateUrl: "./asset-linked-items-section.component.html",
    styleUrl: "./asset-linked-items-section.component.scss",
    imports: [
        AsyncPipe,
        TranslatePipe,
        CerrixButtonComponent,
        CerrixLinkedItemTableComponent,
        CerrixLinkItemsDialogComponent,
        LinkedRiskRowComponent,
        LinkedControlRowComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class AssetLinkedItemsSectionComponent implements OnChanges {
    @Input({ required: true }) assetId: string;

    protected state$: Observable<LinkedRisksState>;
    protected controlsState$: Observable<LinkedControlsState>;
    protected readonly translationScope = scope;
    protected readonly linkDialogName = "asset-link-risks-dialog";
    protected readonly controlLinkDialogName = "asset-link-controls-dialog";
    protected readonly linkableItems = signal<readonly LinkableItem[]>([]);
    protected readonly linkedRiskIds = signal<readonly string[]>([]);
    protected readonly linkableControlItems = signal<readonly LinkableItem[]>([]);
    protected readonly linkedControlIds = signal<readonly string[]>([]);

    private readonly riskDataService = inject(RiskDataService);
    private readonly controlDataService = inject(ControlDataService);
    private readonly tabService = inject(TabService);
    private readonly pages = inject(Pages);
    private readonly toastService = inject(ToastrService);
    private readonly dialogService = inject(CerrixDialogService);
    private readonly translation = injectTranslation(scope);
    private readonly unlinkingRiskIds = new Set<LinkedRiskTableItem["id"]>();
    private readonly unlinkingControlIds = new Set<LinkedControlTableItem["id"]>();
    private openingLinkDialog = false;
    private linking = false;
    private openingControlLinkDialog = false;
    private linkingControls = false;

    protected readonly canLinkRisks$ = this.riskDataService.getAssetLinkPermissions().pipe(
        map((permissions) => permissions.canLinkRisks),
        catchError(() => of(false)),
    );

    protected readonly canLinkControls$ = this.controlDataService.getAssetLinkPermissions().pipe(
        map((permissions) => permissions.canLinkControls),
        catchError(() => of(false)),
    );

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.assetId) {
            this.loadRisks();
            this.loadControls();
        }
    }

    protected loadControls(): void {
        this.controlsState$ = this.controlDataService.getLinkedControls(this.assetId).pipe(
            map(
                (controls): LinkedControlsState => ({
                    status: "loaded",
                    items: controls.map((control) => ({
                        id: control.id,
                        identifier: control.identifier,
                        name: control.name,
                        score: control.score,
                        scoreColor: control.scoreColor,
                        description: control.description,
                        organization: control.organization,
                        controlOwner: control.controlOwner,
                        canUnlink: control.canUnlink,
                    })),
                }),
            ),
            startWith<LinkedControlsState>({ status: "loading" }),
            // The HTTP interceptor reports the error; retain a local failure state for retry.
            catchError(() => of<LinkedControlsState>({ status: "error" })),
        );
    }

    protected readonly loadControlDetails = (
        item: LinkedControlTableItem,
    ): Observable<LinkedControlTableDetails> => {
        return of(item).pipe(map(mapControlRowToDetails));
    };

    protected onOpenLinkedControl(item: LinkedControlTableItem): void {
        this.openControlDetailsTab(item.id);
    }

    protected onOpenLinkableControl(item: LinkableItem): void {
        this.openControlDetailsTab(item.id);
    }

    private openControlDetailsTab(controlGuid: string): void {
        this.tabService.generateTab(this.pages.ControlDetail, controlGuid);
    }

    protected async onLinkControls(
        linkedControls: readonly LinkedControlTableItem[],
    ): Promise<void> {
        if (this.openingControlLinkDialog) {
            return;
        }
        this.openingControlLinkDialog = true;
        try {
            const linkableControls = await firstValueFrom(
                this.controlDataService.getLinkableControlsForAssets(),
            );
            const linkableIdSet = new Set(linkableControls.map((control) => control.id));
            this.linkableControlItems.set([
                ...linkableControls.map((control) => ({
                    id: control.id,
                    identifier: control.identifier,
                    name: control.name,
                    locked: false,
                })),
                // Linked controls the user may not link still show, but the user cannot unlink them.
                ...linkedControls
                    .filter((control) => !linkableIdSet.has(control.id))
                    .map((control) => ({
                        id: control.id,
                        identifier: control.identifier,
                        name: control.name,
                        locked: true,
                    })),
            ]);
            this.linkedControlIds.set(linkedControls.map((control) => control.id));
            this.dialogService.openDialog(this.controlLinkDialogName);
        } catch {
            // The HTTP interceptor reports the error.
        } finally {
            this.openingControlLinkDialog = false;
        }
    }

    protected async onConfirmLinkControls(selected: LinkableItem[]): Promise<void> {
        const controlIds = selected.map((item) => item.id);
        const linkedIds = new Set(this.linkedControlIds());
        const unchanged =
            controlIds.length === linkedIds.size && controlIds.every((id) => linkedIds.has(id));
        if (unchanged || this.linkingControls) {
            return;
        }
        this.linkingControls = true;
        try {
            await firstValueFrom(
                this.controlDataService.setControlsLinkedToAsset(this.assetId, controlIds),
                { defaultValue: undefined },
            );
            await this.translation.ready();
            this.toastService.success(this.translation.t("linkedItems.controls.linksUpdated"));
            this.loadControls();
        } catch {
            // The HTTP interceptor reports the error; the list stays as it was.
        } finally {
            this.linkingControls = false;
        }
    }

    protected async onUnlinkControl(item: LinkedControlTableItem): Promise<void> {
        if (this.unlinkingControlIds.has(item.id)) {
            return;
        }
        this.unlinkingControlIds.add(item.id);
        try {
            await firstValueFrom(this.controlDataService.unlinkAsset(item.id, this.assetId));
            await this.translation.ready();
            this.toastService.success(this.translation.t("linkedItems.controls.unlinkCompleted"));
            this.loadControls();
        } catch {
            // The HTTP interceptor reports the error; the list stays as it was.
        } finally {
            this.unlinkingControlIds.delete(item.id);
        }
    }

    protected loadRisks(): void {
        this.state$ = this.riskDataService.getLinkedRisks(this.assetId).pipe(
            map(
                (risks): LinkedRisksState => ({
                    status: "loaded",
                    items: risks.map((risk) => ({
                        id: risk.id,
                        identifier: risk.identifier,
                        name: risk.name,
                        grossRisk: risk.grossRisk,
                        grossRiskColor: risk.grossRiskColor,
                        netRisk: risk.netRisk,
                        netRiskColor: risk.netRiskColor,
                        canUnlink: risk.canUnlink,
                        confidential: !risk.canViewDetails,
                    })),
                }),
            ),
            startWith<LinkedRisksState>({ status: "loading" }),
            // The HTTP interceptor reports the error; retain a local failure state for retry.
            catchError(() => of<LinkedRisksState>({ status: "error" })),
        );
    }

    protected readonly loadRiskDetails = (
        item: LinkedRiskTableItem,
    ): Observable<LinkedRiskTableDetails> => {
        // A click on a confidential row does not expand it, but the keyboard toggle of the table still does.
        if (item.confidential) {
            void this.onConfidentialRiskClicked();
            return throwError(() => new Error("The risk is confidential."));
        }
        return this.riskDataService.getLinkedRiskDetails(this.assetId, item.id);
    };

    protected async onConfidentialRiskClicked(): Promise<void> {
        await this.translation.ready();
        this.toastService.info(this.translation.t("linkedItems.risks.confidentialDetails"));
    }

    protected onOpenLinkedRisk(item: LinkedRiskTableItem): void {
        this.openRiskDetailsTab(item.id);
    }

    protected onOpenLinkableRisk(item: LinkableItem): void {
        this.openRiskDetailsTab(item.id);
    }

    private openRiskDetailsTab(riskId: string): void {
        this.tabService.generateTab(this.pages.RiskDetail, riskId);
    }

    protected async onLinkRisks(linkedRisks: readonly LinkedRiskTableItem[]): Promise<void> {
        if (this.openingLinkDialog) {
            return;
        }
        this.openingLinkDialog = true;
        try {
            const linkableRisks = await firstValueFrom(
                this.riskDataService.getLinkableRisksForAssets(),
            );
            const linkedIds = linkedRisks.map((risk) => String(risk.id));
            const linkableIdSet = new Set(linkableRisks.map((risk) => risk.id));
            this.linkableItems.set([
                ...linkableRisks.map((risk) => ({
                    id: risk.id,
                    identifier: risk.identifier,
                    name: risk.name,
                    locked: false,
                })),
                // Linked risks the user may not link still show, but the user cannot unlink them.
                ...linkedRisks
                    .filter((risk) => !linkableIdSet.has(String(risk.id)))
                    .map((risk) => ({
                        id: String(risk.id),
                        identifier: risk.identifier,
                        name: risk.name,
                        locked: true,
                    })),
            ]);
            this.linkedRiskIds.set(linkedIds);
            this.dialogService.openDialog(this.linkDialogName);
        } catch {
            // The HTTP interceptor reports the error.
        } finally {
            this.openingLinkDialog = false;
        }
    }

    protected async onConfirmLinkRisks(selected: LinkableItem[]): Promise<void> {
        const riskIds = selected.map((item) => item.id);
        const linkedIds = new Set(this.linkedRiskIds());
        const unchanged =
            riskIds.length === linkedIds.size && riskIds.every((id) => linkedIds.has(id));
        if (unchanged || this.linking) {
            return;
        }
        this.linking = true;
        try {
            await firstValueFrom(
                this.riskDataService.setRisksLinkedToAsset(this.assetId, riskIds),
                {
                    defaultValue: undefined,
                },
            );
            await this.translation.ready();
            this.toastService.success(this.translation.t("linkedItems.risks.linksUpdated"));
            this.loadRisks();
        } catch {
            // The HTTP interceptor reports the error; the list stays as it was.
        } finally {
            this.linking = false;
        }
    }

    protected async onUnlinkRisk(item: LinkedRiskTableItem): Promise<void> {
        if (this.unlinkingRiskIds.has(item.id)) {
            return;
        }
        this.unlinkingRiskIds.add(item.id);
        try {
            await firstValueFrom(this.riskDataService.unlinkAsset(String(item.id), this.assetId));
            await this.translation.ready();
            this.toastService.success(this.translation.t("linkedItems.risks.unlinkCompleted"));
            this.loadRisks();
        } catch {
            // The HTTP interceptor reports the error; the list stays as it was.
        } finally {
            this.unlinkingRiskIds.delete(item.id);
        }
    }
}
